/**
 * Active memberships in a shop whose EFFECTIVE roles include any of `roles`.
 *
 * Multi-role: `.in('role', [...])` asked the single legacy scalar, which keeps only ONE of a
 * person's roles — a technician who also writes repair orders is stored as TECHNICIAN, so every
 * "who are the advisors?" query silently dropped them. This asks the same projection
 * `usePermissions` and `useFohCapableMembers` use (`actsAsLegacyRole` over the membership AND its
 * work roles). Because the projection always includes the legacy scalar, the result is a superset
 * of the old query: nobody the old query found is lost.
 *
 * Each returned row's `role` is the FIRST of `roles` the member acts as, so callers that branch on
 * `row.role` (labels, counts) keep working unchanged.
 *
 * A failed read THROWS — "nobody has work roles" is not a safe default (it is the legacy answer).
 */
import { actsAsLegacyRole } from '@/lib/roles/multiRole';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Client = { from: (table: string) => any };

export interface MembershipActingAs {
  user_id: string;
  /** The first of the requested roles this member effectively acts as. */
  role: string;
  /** The membership's legacy scalar (Core-100 direction 12: tells a Tech + SA from front-office staff). */
  legacy_role?: string;
}

export async function fetchMembershipsActingAs(
  client: Client,
  shopId: string,
  roles: readonly string[],
): Promise<MembershipActingAs[]> {
  const { data, error } = await client
    .from('memberships')
    .select('id, user_id, role, authority_role')
    .eq('shop_id', shopId)
    .eq('status', 'active');
  if (error) throw error;

  const raw = (data ?? []) as Array<{ id?: string; user_id: string; role: string; authority_role?: string | null }>;
  const ids = raw.map((r) => r.id).filter((x): x is string => !!x);
  const workRoles = new Map<string, string[]>();
  if (ids.length > 0) {
    const { data: wr, error: wrError } = await client
      .from('membership_work_roles')
      .select('membership_id, work_role')
      .in('membership_id', ids);
    if (wrError) throw wrError;
    for (const row of (wr ?? []) as Array<{ membership_id: string; work_role: string }>) {
      const list = workRoles.get(row.membership_id) ?? [];
      list.push(row.work_role);
      workRoles.set(row.membership_id, list);
    }
  }

  const out: MembershipActingAs[] = [];
  for (const row of raw) {
    const member = {
      role: row.role,
      authority_role: row.authority_role ?? null,
      work_roles: row.id ? (workRoles.get(row.id) ?? []) : null,
    } as never;
    const first = roles.find((r) => actsAsLegacyRole(member, r));
    if (first) out.push({ user_id: row.user_id, role: first, legacy_role: row.role });
  }
  return out;
}
